import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MediaItem, MediaFile } from '../types';
import { doc, updateDoc, getFirestore, getDoc, arrayUnion } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';

interface EditMediaComponentProps {
    item: MediaItem;
    onMediaUpdate: (updatedMedia: MediaFile[]) => void;
}

interface Tick {
    position: number;
    major: boolean;
    label: string;
    value: number;
}

export const EditMediaComponent: React.FC<EditMediaComponentProps> = ({ item, onMediaUpdate }) => {
    const { currentUser } = useAuth();
    const [showTrimmer, setShowTrimmer] = useState(false);

    // Find first video index or default to 0
    const getInitialMediaIndex = () => {
        if (!item.media || item.media.length === 0) return 0;
        const videoIndex = item.media.findIndex(media => media.type === 'video');
        return videoIndex >= 0 ? videoIndex : 0;
    };

    const [selectedMediaIndex, setSelectedMediaIndex] = useState(getInitialMediaIndex());

    // Trimmer state
    const [videoDuration, setVideoDuration] = useState(0);
    const [startTime, setStartTime] = useState(0);
    const [endTime, setEndTime] = useState(10);
    const [currentPlayTime, setCurrentPlayTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragType, setDragType] = useState<'start' | 'end' | null>(null);
    const [ticks, setTicks] = useState<Tick[]>([]);

    const videoRef = useRef<HTMLVideoElement>(null);
    const rulerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Format time for display (mm:ss format)
    const formatTime = (seconds: number): string => {
        if (isNaN(seconds)) return '00:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Format time for tick labels
    const formatTimeForLabel = (seconds: number): string => {
        if (isNaN(seconds)) return '0';
        if (seconds >= 60) return formatTime(seconds);
        return Math.floor(seconds).toString();
    };

    // Determine the appropriate interval for major ticks
    const getMajorTickInterval = (duration: number): number => {
        if (duration <= 10) return 1;
        if (duration <= 60) return 5;
        if (duration <= 300) return 10;
        return 30;
    };

    // Generate tick marks for the ruler
    const generateTicks = useCallback((duration: number): void => {
        const newTicks: Tick[] = [];
        const majorInterval = getMajorTickInterval(duration);
        const minorTicksPerMajor = 10;

        for (let i = 0; i <= duration; i += majorInterval) {
            if (i > duration) break;
            const position = (i / duration) * 100;
            newTicks.push({
                position,
                major: true,
                label: formatTimeForLabel(i),
                value: i
            });

            if (i + majorInterval <= duration) {
                const minorInterval = majorInterval / minorTicksPerMajor;
                for (let j = 1; j < minorTicksPerMajor; j++) {
                    const minorTime = i + (j * minorInterval);
                    if (minorTime >= duration) break;
                    const minorPosition = (minorTime / duration) * 100;
                    newTicks.push({
                        position: minorPosition,
                        major: false,
                        label: '',
                        value: minorTime
                    });
                }
            }
        }
        setTicks(newTicks);
    }, []);

    // Handle mouse/touch movement during drag
    const handleMove = useCallback((clientX: number) => {
        if (!isDragging || !dragType || !rulerRef.current) return;

        const rulerRect = rulerRef.current.getBoundingClientRect();
        const rulerWidth = rulerRect.width;
        const offsetX = clientX - rulerRect.left;

        let newTime = (offsetX / rulerWidth) * videoDuration;
        newTime = Math.round(newTime * 10) / 10;
        newTime = Math.max(0, Math.min(videoDuration, newTime));

        if (dragType === 'start') {
            newTime = Math.min(newTime, endTime - 0.5);
            setStartTime(newTime);
        } else {
            newTime = Math.max(newTime, startTime + 0.5);
            setEndTime(newTime);
        }

        if (videoRef.current) {
            videoRef.current.currentTime = newTime;
        }
    }, [isDragging, dragType, videoDuration, endTime, startTime]);

    const handleMouseMove = useCallback((event: MouseEvent) => {
        handleMove(event.clientX);
    }, [handleMove]);

    const handleTouchMove = useCallback((event: TouchEvent) => {
        event.preventDefault();
        if (event.touches.length > 0) {
            handleMove(event.touches[0].clientX);
        }
    }, [handleMove]);

    // Handle mouse/touch end
    const handleEnd = useCallback(() => {
        setIsDragging(false);
        setDragType(null);
    }, []);

    // Setup drag event listeners for both mouse and touch
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleEnd);
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('touchend', handleEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleEnd);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleEnd);
        };
    }, [isDragging, handleMouseMove, handleTouchMove, handleEnd]);

    // Load video metadata when trimmer is shown
    useEffect(() => {
        if (!showTrimmer || !videoRef.current) return;

        const video = videoRef.current;

        const handleLoadedMetadata = () => {
            const duration = video.duration;
            if (duration && !isNaN(duration) && duration > 0) {
                setVideoDuration(duration);
                setStartTime(0);
                setEndTime(Math.min(10, duration));
                generateTicks(duration);
            }
        };

        const handleTimeUpdate = () => {
            const currentTime = video.currentTime;
            setCurrentPlayTime(currentTime);
        };

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleEnded = () => setIsPlaying(false);

        video.load();
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('ended', handleEnded);

        const checkDuration = () => {
            if (video.duration && !isNaN(video.duration) && video.duration > 0) {
                handleLoadedMetadata();
            } else {
                setTimeout(checkDuration, 100);
            }
        };

        if (video.readyState >= 1) {
            handleLoadedMetadata();
        } else {
            checkDuration();
        }

        return () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('ended', handleEnded);
        };
    }, [showTrimmer, generateTicks]);

    const trimDuration = endTime - startTime;

    const getVideoMedia = (): MediaFile | null => {
        if (!item.media || item.media.length === 0) return null;
        return item.media.find(media => media.type === 'video') || null;
    };

    const getCurrentMedia = (): MediaFile => {
        // Ensure selectedMediaIndex is within bounds
        const validIndex = Math.max(0, Math.min(selectedMediaIndex, item.media.length - 1));
        return item.media[validIndex] || null;
    };

    const getVideoUrl = (): string => {
        const videoMedia = getVideoMedia();
        return videoMedia ? videoMedia.downloadURL : (item.url || '');
    };

    const startDrag = (event: React.MouseEvent | React.TouchEvent, type: 'start' | 'end') => {
        setIsDragging(true);
        setDragType(type);
        event.preventDefault();
    };

    const togglePlayPause = () => {
        const video = videoRef.current;
        if (!video) return;

        if (isPlaying) {
            video.pause();
        } else {
            if (video.currentTime >= endTime || video.currentTime < startTime) {
                video.currentTime = startTime;
            }
            video.play();

            const checkTime = () => {
                if (video.currentTime >= endTime) {
                    video.pause();
                    video.removeEventListener('timeupdate', checkTime);
                }
            };

            video.addEventListener('timeupdate', checkTime);
        }
    };

    const trimVideo = async () => {
        const videoMedia = getVideoMedia();
        if (!videoMedia) return;

        setLoading(true);
        try {
            const response = await fetch('https://media-transcoder-255551438908.us-central1.run.app/trim-video', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    filePath: videoMedia.path,
                    startAt: startTime,
                    endAt: endTime
                })
            });

            if (!response.ok) {
                throw new Error(`Server error: ${response.statusText}`);
            }

            const result = await response.json();

            // Update the media array with the trimmed video URL
            if (item.media && videoMedia) {
                const index = item.media.findIndex(mediaItem => mediaItem.path === videoMedia.path);
                if (index !== -1) {
                    item.media[index].downloadURL = result.downloadURL;
                    const updatedMedia = [...item.media];
                    onMediaUpdate(updatedMedia);
                }
            }

            setShowTrimmer(false);
        } catch (error) {
            console.error('Error trimming video:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateFileName = (prefix: string = 'sellox', index: number = 0): string => {
        const timestamp = new Date().getTime();
        return `${prefix}-${timestamp}-${index}.jpg`;
    };

    const captureFrame = async () => {
        const video = videoRef.current;
        if (!video) return;

        const videoMedia = getVideoMedia();
        if (!videoMedia) return;

        setLoading(true);
        try {
            const response = await fetch('https://media-transcoder-255551438908.us-central1.run.app/get-image', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    filePath: videoMedia.path,
                    timeInSeconds: video.currentTime,
                    quality: 95
                })
            });

            if (!response.ok) {
                throw new Error(`Server error: ${response.statusText}`);
            }

            const result = await response.json();

            const newMediaItem: MediaFile = {
                downloadURL: result.imageURL,
                path: result.imageURL.split('/').pop(),
                type: 'image' as const,
                primary: false
            };

            // Update only local state, not Firestore
            const updatedMedia = item.media ? [...item.media, newMediaItem] : [newMediaItem];
            onMediaUpdate(updatedMedia);

        } catch (error) {
            console.error('Error capturing frame:', error);
            alert('Failed to capture frame');
        } finally {
            setLoading(false);
        }
    };

    const markAsPrimary = (mediaIndex: number) => {
        if (!item.media) return;

        const updatedMedia = item.media.map((media, index) => ({
            ...media,
            primary: index === mediaIndex
        }));

        onMediaUpdate(updatedMedia);
    };

    // CDN thumbnail function matching Angular implementation
    const getCdnUrl = (downloadURL: string) => {
        if (!downloadURL) return '';
        try {
            let path = new URL(downloadURL).pathname;
            path = path.replace(/.*.com/, '');
            path = path.substring(3);

            return `https://cdn.sellox.app/preview/${path}?auto=compress&format=webp&qlt=100&fit=crop`;
        } catch (error) {
            console.error('Error generating CDN URL:', error);
            return downloadURL;
        }
    };

    const currentMedia = getCurrentMedia();

    // Update selected index when item changes
    useEffect(() => {
        const newIndex = getInitialMediaIndex();
        setSelectedMediaIndex(newIndex);
    }, [item.media]);

    // Debug logging
    console.log('EditMediaComponent Debug:', {
        currentMedia,
        selectedMediaIndex,
        itemMedia: item.media,
        videoUrl: currentMedia?.type === 'video' ? getVideoUrl() : 'N/A',
        hasVideo: item.media?.some(m => m.type === 'video')
    });

    return (
        <div className="relative w-full">
            {/* Main Media Display - Full Screen */}
            <div className="inset-0 bg-black">
                {currentMedia?.type === 'video' ? (
                    <div className="relative h-full">
                        <video
                            ref={videoRef}
                            src={getVideoUrl()}
                            controls={!showTrimmer}
                            className="w-full min-h-[80vh] object-cover cursor-pointer"
                            onClick={showTrimmer ? togglePlayPause : undefined}
                            playsInline
                            preload="metadata"
                        />

                        {!showTrimmer ? (
                            <div className="absolute top-4 left-4 flex gap-2">
                                <button
                                    onClick={() => setShowTrimmer(true)}
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm flex items-center gap-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                    Trim
                                </button>
                                {/* <button
                                    onClick={captureFrame}
                                    disabled={loading}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm flex items-center gap-1 disabled:opacity-50"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Snap Frame
                                </button> */}
                            </div>
                        ) : (
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                                <div className="flex items-center justify-between text-white mb-4">
                                    <button onClick={togglePlayPause} className="hover:bg-white/20 rounded-full p-2">
                                        {isPlaying ? (
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                            </svg>
                                        ) : (
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        )}
                                    </button>
                                    <div className="text-sm">
                                        {formatTime(currentPlayTime)} / {formatTime(videoDuration)}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={togglePlayPause}
                                            className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full"
                                        >
                                            {isPlaying ? (
                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </button>
                                        <button
                                            onClick={captureFrame}
                                            disabled={loading}
                                            className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full disabled:opacity-50"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={trimVideo}
                                            disabled={loading}
                                            className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full disabled:opacity-50"
                                        >
                                            {loading ? (
                                                <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => setShowTrimmer(false)}
                                            className="text-white/80 hover:text-white p-2 rounded-full"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Trimmer UI */}
                                <div className="bg-black/30 rounded-lg p-4">
                                    <div className="flex justify-between items-center mb-4 text-sm">
                                        <div className="text-center">
                                            <div className="text-white/70">Start</div>
                                            <div className="font-medium text-white">{formatTime(startTime)}</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-white/70">Duration</div>
                                            <div className="font-medium text-white">{formatTime(trimDuration)}</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-white/70">End</div>
                                            <div className="font-medium text-white">{formatTime(endTime)}</div>
                                        </div>
                                    </div>

                                    <div className="relative">
                                        <div ref={rulerRef} className="relative h-16 bg-gray-800 rounded-lg overflow-hidden">
                                            {/* Ticks */}
                                            {ticks.map((tick, index) => (
                                                <div
                                                    key={index}
                                                    className={`absolute top-0 ${tick.major ? 'h-8 bg-gray-400' : 'h-4 bg-gray-500'} w-px`}
                                                    style={{ left: `${tick.position}%` }}
                                                >
                                                    {tick.major && (
                                                        <div className="absolute top-8 left-0 transform -translate-x-1/2 text-xs text-white/70">
                                                            {tick.label}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            {/* Selection indicator */}
                                            <div
                                                className="absolute top-0 h-full bg-red-500 opacity-30"
                                                style={{
                                                    left: `${(startTime / videoDuration) * 100}%`,
                                                    width: `${(trimDuration / videoDuration) * 100}%`
                                                }}
                                            />

                                            {/* Playhead */}
                                            {!isDragging && videoDuration > 0 && (
                                                <div
                                                    className="absolute top-0 h-full w-1 bg-blue-400 z-10"
                                                    style={{
                                                        left: `${Math.min(100, Math.max(0, (currentPlayTime / videoDuration) * 100))}%`,
                                                        transform: 'translateX(-50%)'
                                                    }}
                                                >
                                                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-blue-400 rounded-full"></div>
                                                </div>
                                            )}

                                            {/* Start handle */}
                                            <div
                                                className="absolute top-0 h-full w-12 bg-transparent cursor-ew-resize z-20 flex items-center justify-center"
                                                style={{ left: `max(0.75rem, ${(startTime / videoDuration) * 100}% - 0.75rem)` }}
                                                onMouseDown={(e) => startDrag(e, 'start')}
                                                onTouchStart={(e) => startDrag(e, 'start')}
                                            >
                                                <div className="w-1.5 h-10 bg-red-500 rounded-full shadow-md"></div>
                                            </div>

                                            {/* End handle */}
                                            <div
                                                className="absolute top-0 h-full w-12 bg-transparent cursor-ew-resize z-20 flex items-center justify-center"
                                                style={{ right: `max(0.75rem, 100% - ${(endTime / videoDuration) * 100}% - 0.75rem)` }}
                                                onMouseDown={(e) => startDrag(e, 'end')}
                                                onTouchStart={(e) => startDrag(e, 'end')}
                                            >
                                                <div className="w-1.5 h-10 bg-red-500 rounded-full shadow-md"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <img
                        src={getCdnUrl(currentMedia.downloadURL)}
                        alt="Media item"
                        className="w-full min-h-[80vh] object-cover"
                    />
                )}
            </div>

            {/* Right Panel - Thumbnails */}
            <div className="w-full md:w-80 bg-gray-100 border-l border-gray-200 overflow-y-auto">
                <div className="p-4">
                    <h3 className="text-gray-900 text-lg font-semibold mb-4">Media Gallery</h3>

                    <div className="grid grid-cols-3 md:grid-cols-2 gap-3">
                        {item.media?.map((media, index) => (
                            <div
                                key={index}
                                className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 touch-manipulation ${selectedMediaIndex === index ? 'border-red-500 shadow-md' : 'border-gray-300 hover:border-gray-400 hover:shadow-sm'
                                    }`}
                                onClick={() => setSelectedMediaIndex(index)}
                            >
                                {media.type !== 'video' ? (
                                    <img
                                        src={getCdnUrl(media.downloadURL)}
                                        alt={`Media ${index + 1}`}
                                        className="w-full h-20 md:h-24 object-cover"
                                    />
                                ) : (
                                    <div className="relative">
                                        <video
                                            src={media.downloadURL}
                                            className="w-full h-20 md:h-24 object-cover"
                                            muted
                                            preload="metadata"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                )}
                                {/* Primary indicator and button */}
                                <div className="absolute top-2 right-2">
                                    {media.primary ? (
                                        <div className="bg-yellow-400 text-black text-xs px-2 py-1 rounded-full font-medium shadow-lg flex items-center gap-1">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                            Primary
                                        </div>
                                    ) : (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                markAsPrimary(index);
                                            }}
                                            className="bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full transition-all duration-200 shadow-lg hover:scale-110"
                                            title="Set as primary image"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Hidden canvas for frame capture */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
    );
};

export default EditMediaComponent;
